import React, { useEffect, useState } from 'react';
import { apiFetch } from '../../utils/api';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from 'recharts';

const COLORS = ['#0d7a28', 'var(--blue-500)', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899'];

export default function Reports() {
  const [overview, setOverview] = useState(null);
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([apiFetch('/reports/overview'), apiFetch('/reports/agents')])
      .then(([o, a]) => { setOverview(o); setAgents(a); })
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div style={{ display: 'flex', justifyContent: 'center', padding: 80 }}><span className="spinner" /></div>;

  const totalResolved = (overview.resolved || 0) + (overview.closed || 0);
  const resolutionRatio = overview.total > 0 ? Math.round((totalResolved / overview.total) * 100) : 0;
  const slaBreachPct   = overview.total > 0 ? Math.round(((overview.slaBreached || 0) / overview.total) * 100) : 0;
  const onTimePct      = totalResolved > 0 ? Math.round(((overview.resolvedOnTime || 0) / totalResolved) * 100) : 0;

  const KPI_CARDS = [
    {
      label: 'Resolution Ratio',
      sublabel: 'Resolved / Received',
      value: `${resolutionRatio}%`,
      color: resolutionRatio >= 70 ? 'var(--status-resolved-text)' : resolutionRatio >= 40 ? '#f59e0b' : '#ef4444',
      bg: resolutionRatio >= 70 ? 'var(--status-resolved-bg)' : resolutionRatio >= 40 ? 'var(--amber-50)' : 'var(--red-50)',
      icon: '✅',
    },
    {
      label: 'SLA Breach Rate',
      sublabel: 'Breached / Total',
      value: `${slaBreachPct}%`,
      color: slaBreachPct === 0 ? 'var(--status-resolved-text)' : slaBreachPct <= 10 ? '#f59e0b' : '#ef4444',
      bg: slaBreachPct === 0 ? 'var(--status-resolved-bg)' : slaBreachPct <= 10 ? 'var(--amber-50)' : 'var(--red-50)',
      icon: '⏱',
    },
    {
      label: 'On-Time Resolution',
      sublabel: 'Resolved before SLA',
      value: `${onTimePct}%`,
      color: onTimePct >= 80 ? 'var(--status-resolved-text)' : onTimePct >= 50 ? '#f59e0b' : '#ef4444',
      bg: onTimePct >= 80 ? 'var(--status-resolved-bg)' : onTimePct >= 50 ? 'var(--amber-50)' : 'var(--red-50)',
      icon: '🎯',
    },
  ];

  return (
    <div style={{ maxWidth: 1200, margin: '0 auto', padding: '32px 24px' }}>
      <h1 style={{ fontSize: 22, fontWeight: 700, marginBottom: 24 }}>Reports & Analytics</h1>

      {/* KPI metric cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16, marginBottom: 28 }}>
        {KPI_CARDS.map(card => (
          <div key={card.label} className="card" style={{ padding: '20px 24px', display: 'flex', alignItems: 'center', gap: 18 }}>
            <div style={{ width: 48, height: 48, borderRadius: 12, background: card.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22, flexShrink: 0 }}>
              {card.icon}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 28, fontWeight: 800, color: card.color, lineHeight: 1 }}>{card.value}</div>
              <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)', marginTop: 4 }}>{card.label}</div>
              <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 2 }}>{card.sublabel}</div>
            </div>
          </div>
        ))}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 20, marginBottom: 28 }}>
        <div className="card" style={{ padding: 24 }}>
          <h2 style={{ fontSize: 15, fontWeight: 600, marginBottom: 16 }}>By Status</h2>
          <ResponsiveContainer width="100%" height={200}>
            <PieChart>
              <Pie data={overview.byStatus} dataKey="count" nameKey="status" cx="50%" cy="50%" outerRadius={70} label={({ status, percent }) => `${status} ${(percent * 100).toFixed(0)}%`}>
                {overview.byStatus.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </div>

        <div className="card" style={{ padding: 24 }}>
          <h2 style={{ fontSize: 15, fontWeight: 600, marginBottom: 16 }}>By Priority</h2>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={overview.byPriority} layout="vertical">
              <XAxis type="number" tick={{ fontSize: 11 }} allowDecimals={false} />
              <YAxis type="category" dataKey="priority" tick={{ fontSize: 12 }} width={60} />
              <Tooltip />
              <Bar dataKey="count" fill="#0d7a28" radius={[0,4,4,0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="card" style={{ padding: 24 }}>
          <h2 style={{ fontSize: 15, fontWeight: 600, marginBottom: 16 }}>By Category</h2>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={overview.byCategory} layout="vertical">
              <XAxis type="number" tick={{ fontSize: 11 }} allowDecimals={false} />
              <YAxis type="category" dataKey="category" tick={{ fontSize: 11 }} width={90} />
              <Tooltip />
              <Bar dataKey="count" fill="var(--blue-500)" radius={[0,4,4,0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="card" style={{ padding: 24, marginBottom: 24 }}>
        <h2 style={{ fontSize: 15, fontWeight: 600, marginBottom: 16 }}>SLA Summary</h2>
        <div style={{ display: 'flex', gap: 24 }}>
          <div style={{ textAlign: 'center' }}>
            <p style={{ fontSize: 36, fontWeight: 700, color: overview.slaBreached > 0 ? '#b91c1c' : 'var(--status-resolved-text)' }}>{overview.slaBreached}</p>
            <p style={{ fontSize: 13, color: 'var(--text-secondary)' }}>SLA Breached</p>
          </div>
          <div style={{ textAlign: 'center' }}>
            <p style={{ fontSize: 36, fontWeight: 700, color: 'var(--status-resolved-text)' }}>{overview.avgResolutionHours ? `${overview.avgResolutionHours.toFixed(1)}h` : '—'}</p>
            <p style={{ fontSize: 13, color: 'var(--text-secondary)' }}>Avg Resolution Time</p>
          </div>
          <div style={{ textAlign: 'center' }}>
            <p style={{ fontSize: 36, fontWeight: 700, color: 'var(--blue-700)' }}>{overview.total}</p>
            <p style={{ fontSize: 13, color: 'var(--text-secondary)' }}>Total Tickets</p>
          </div>
        </div>
      </div>

      <div className="card" style={{ padding: 24 }}>
        <h2 style={{ fontSize: 15, fontWeight: 600, marginBottom: 16 }}>Agent Performance</h2>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
          <thead>
            <tr style={{ borderBottom: '1px solid var(--border-subtle)' }}>
              {['Agent', 'Email', 'Assigned', 'Resolved', 'Open', 'Avg Resolution (h)'].map(h => (
                <th key={h} style={{ padding: '8px 12px', textAlign: 'left', color: 'var(--text-secondary)', fontWeight: 500, fontSize: 12 }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {agents.map(a => (
              <tr key={a.id} style={{ borderBottom: '1px solid var(--border-subtle)' }}>
                <td style={{ padding: '12px 12px', fontWeight: 500 }}>{a.name}</td>
                <td style={{ padding: '12px 12px', color: 'var(--text-secondary)' }}>{a.email}</td>
                <td style={{ padding: '12px 12px' }}>{a.total_assigned}</td>
                <td style={{ padding: '12px 12px', color: '#0d7a28', fontWeight: 500 }}>{a.resolved}</td>
                <td style={{ padding: '12px 12px', color: 'var(--blue-700)' }}>{a.open}</td>
                <td style={{ padding: '12px 12px', color: 'var(--text-secondary)' }}>{a.avg_resolution_hours ? a.avg_resolution_hours.toFixed(1) : '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
